import { useEffect, useState, type ReactNode } from "react";
import { isoCountryName } from "@/app/(app)/_lib/iso-countries";
import {
  ADVERSE_MEDIA_COUNTRIES_STRICT_LABEL,
  ADVERSE_MEDIA_KEYWORD_OPTIONS,
  ADVERSE_MEDIA_LANGUAGE_OPTIONS,
  daysBackLabel,
  hasAdverseMediaSettings,
  listSearchConfigurations,
  type AdverseMediaConfig,
} from "@/src/services/search_configurations_service";

export const ADVERSE_MEDIA_COLLECTION_ID = "coll:adverse-media";
export const ADVERSE_MEDIA_RESTRICTED_ENTITY_TYPES = ["Country", "Aircraft", "Vessel"] as const;

export const ADVERSE_MEDIA_UNUSED_FIELD_TOOLTIP = "Adverse Media only searches by name";

const languageLabel = (v: string) =>
  ADVERSE_MEDIA_LANGUAGE_OPTIONS.find((o) => o.value === v)?.label ?? v;

const keywordLabel = (v: string) =>
  ADVERSE_MEDIA_KEYWORD_OPTIONS.find((o) => o.value === v)?.label ?? v;

const countryLabel = (v: string) => isoCountryName(v) ?? v;

export function buildAdverseMediaTooltip(am: AdverseMediaConfig | null): ReactNode {
  const tooltipGroup = (label: string, values: string) => (
    <span className="block" key={label}>
      <span className="font-semibold text-white">{label}:</span> {values}
    </span>
  );
  const countries =
    am?.country === null
      ? ADVERSE_MEDIA_COUNTRIES_STRICT_LABEL
      : am && am.country.length
        ? am.country.map(countryLabel).join(", ")
        : "All";
  const languages = am && am.language.length ? am.language.map(languageLabel).join(", ") : "All";
  const keywords = am && am.keywords.length ? am.keywords.map(keywordLabel).join(", ") : "All";
  const minScore = am && am.minScore != null ? `${Math.round(am.minScore * 100)}%` : "Default";
  const timeHorizon = daysBackLabel(am?.daysBack ?? null);
  return (
    <>
      {tooltipGroup("Countries", countries)}
      {tooltipGroup("Languages", languages)}
      {tooltipGroup("Keywords", keywords)}
      {tooltipGroup("Min score", minScore)}
      {tooltipGroup("Time horizon", timeHorizon)}
    </>
  );
}

export function useCompanyAdverseMedia() {
  const [companyAdverseMedia, setCompanyAdverseMedia] = useState<AdverseMediaConfig | null>(null);
  const [companyConfigLoaded, setCompanyConfigLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listSearchConfigurations().then((res) => {
      if (cancelled || !res.ok) return;
      const companyCfg = res.value.find((c) => c.type === "company" && c.adverseMedia);
      const am = companyCfg?.adverseMedia ?? null;
      setCompanyAdverseMedia(hasAdverseMediaSettings(am) ? am : null);
      setCompanyConfigLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    companyAdverseMedia,
    companyConfigLoaded,
    hasCompanyAdverseMediaSettings: !!companyAdverseMedia,
  };
}
